/**
 * What the billing record means for the customer: which plan they are on,
 * whether they pay for it, what happens at the end of the period, and what
 * they can do about it. Pure functions, so every state is unit tested
 * (billingModel.test.ts) and every billing screen reads the same answer.
 */
import type { StatusTone } from '../../theme';
import { formatBytes, formatDate, formatMoney } from '../../utils/format';
import type { BillingInfo, PlanIncludes, PlanLimits, SubscriptionPlan } from './billingTypes';

/** The free base plan. Its id stays 'basic' for existing billing records. */
export const FREE_PLAN_ID = 'basic';

/** A plan with nothing to pay: no checkout, no billing date. */
export const isFreePlan = (plan?: Pick<SubscriptionPlan, 'id' | 'price'> | null): boolean =>
  Boolean(plan) && !(Number(plan!.price) > 0);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A renewing Stripe subscription has its period end moved forward by a webhook
 * at every renewal, so an ACTIVE record whose period ended this long ago has
 * no renewal on file. That changes the wording only, never the actions: the
 * record may still bill (Moneris recurring renewals never update the period
 * end, and Stripe webhooks can fail), so Cancel and "Update card" stay.
 * More than a full monthly cycle, so a delayed or retried webhook never trips it.
 */
export const STALE_SUBSCRIPTION_DAYS = 35;

/**
 * Storage may go this far over the plan before new uploads are blocked
 * (Terms section 4; the API's limit enforcer allows the same 10%).
 */
export const STORAGE_OVERAGE_ALLOWANCE = 0.1;

/** A plan id as a name, for history rows and plans the API didn't list. */
export const formatPlanIdForDisplay = (planId?: string | null): string => {
  switch (planId) {
    case null:
    case undefined:
    case '':
      return '—';
    case 'cancelled':
      return 'Cancelled';
    case 'cancelled-at-period-end':
      return 'Cancel scheduled';
    case FREE_PLAN_ID:
      return 'Free';
    case 'pro':
      return 'Pro';
    case 'promax':
      return 'Pro Max';
    default: {
      const words = planId.replace(/[-_]+/g, ' ').trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    }
  }
};

/** The billing currency as an ISO code; CAD when the record has none (plans are CAD-first). */
export const billingCurrency = (currency?: string | null): string =>
  typeof currency === 'string' && /^[a-z]{3}$/i.test(currency.trim()) ? currency.trim().toUpperCase() : 'CAD';

/** A Date, or null when the value is missing or unreadable. */
export const parseDate = (value: string | number | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Cheapest first: the order people compare plans in, and the order upgrades are judged by. */
export const sortPlansByPrice = (plans: SubscriptionPlan[]): SubscriptionPlan[] =>
  plans.slice().sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));

/** A count limit: "3", or "Unlimited" for -1. */
export const formatLimit = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value < 0 ? 'Unlimited' : String(value);
};

/** A byte limit: "10 GB", or "Unlimited" for -1. */
export const formatSizeLimit = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes === 0) return '—';
  return formatBytes(bytes);
};

export interface PlanFact {
  label: string;
  value: string;
  /** The plan doesn't include this: the value is a dash, read out as "Not included". */
  missing?: boolean;
}

const unlocks = (label: string, included: boolean): PlanFact =>
  included ? { label, value: 'Included' } : { label, value: '—', missing: true };

/**
 * The same rows, in the same order, on every plan: its limits, then what it
 * unlocks. An older API that doesn't say what a plan unlocks gets the limits only.
 */
export const planFacts = (limits?: PlanLimits | null, includes?: PlanIncludes | null): PlanFact[] => [
  { label: 'Storage', value: formatSizeLimit(limits?.storage) },
  { label: 'Largest file', value: formatSizeLimit(limits?.uploadSize) },
  { label: 'Active projects', value: formatLimit(limits?.projects) },
  { label: 'Change rounds per project', value: formatLimit(limits?.revisions) },
  ...(includes
    ? [
        unlocks('AI task suggestions', includes.aiTaskSuggestions),
        unlocks('AI transcripts and editing from the transcript', includes.transcripts),
        unlocks('Suggested camera cuts', includes.suggestCuts),
      ]
    : []),
];

/** Under the plan cards: what every plan has, so the rows above stay about the differences. */
export const EVERY_PLAN_NOTE =
  'Every plan: multicam with sync by sound, Live cut and rough cut; frame-accurate notes; editors never pay.';

/** "CA$14.99 per month". */
export const formatMonthlyPrice = (price: number, currency: string): string =>
  `${formatMoney(Number.isFinite(price) ? price : 0, billingCurrency(currency))} per month`;

/** A plan's price: "Free forever" (the Terms' wording), or "CA$14.99 per month + tax". Never "$0.00". */
export const formatPlanPrice = (plan: Pick<SubscriptionPlan, 'id' | 'price'>, currency: string): string =>
  isFreePlan(plan) ? 'Free forever' : `${formatMonthlyPrice(Number(plan.price), currency)} + tax`;

export type SubscriptionPhase =
  /** No subscription bills this account. */
  | 'none'
  /** Paid, and renews at the period end. */
  | 'renewing'
  /** Cancelled at the period end: paid up until then, then Free. */
  | 'ending'
  /** A change to another plan is scheduled for the period end. */
  | 'changing'
  /** The last payment failed (past due or in the grace period). */
  | 'payment_issue';

export interface BillingSummary {
  planId: string;
  plan?: SubscriptionPlan;
  /** The plan as people read it: "Free", "Pro", or "Basic" for a legacy paid Basic subscription. */
  planName: string;
  /** The current price list has this plan at $0 (the Free plan, id 'basic'). */
  planIsFree: boolean;
  /** The API status, upper-cased: ACTIVE, PAST_DUE, CANCELLING, GRACE_PERIOD, CANCELLED. */
  status: string;
  /** Whether a provider subscription bills this account, now or until its period end. */
  hasLiveSubscription: boolean;
  /**
   * A live subscription on the 'basic' id: the original paid Basic plan
   * ($0.99/month) from before Basic became the $0 Free plan. It still bills.
   */
  isLegacyPaidBasic: boolean;
  /** The record still carries a subscription id, but that subscription has ended (CANCELLED). */
  hasStaleSubscriptionRef: boolean;
  /**
   * ACTIVE, but no renewal recorded for more than STALE_SUBSCRIPTION_DAYS
   * after the period end. Changes the wording only; Cancel stays available.
   */
  renewalUnconfirmed: boolean;
  phase: SubscriptionPhase;
  periodEnd: Date | null;
  /** "Nov 15, 2026", or null when the record has no readable period end. */
  periodEndLabel: string | null;
  /** The period end is now or earlier (a renewal webhook may still be on its way). */
  periodEndPassed: boolean;
  /** The plan a scheduled change (or a cancellation, which lands on Free) moves to. */
  nextPlanId: string | null;
  nextPlan?: SubscriptionPlan;
  nextPlanName: string | null;
  /**
   * The monthly price this subscriber pays, before tax. The API doesn't report
   * a subscriber's own price, so this is the plan's list price; null for Free
   * and for legacy Basic, whose price isn't on the current list.
   */
  monthlyPrice: number | null;
  currency: string;
  /** Cancel is offered: anything that bills and isn't already cancelling. */
  canCancel: boolean;
  /** "Keep <plan>" is offered: a cancellation at the period end can be undone. */
  canKeepPlan: boolean;
  /** The last payment failed. */
  hasPaymentIssue: boolean;
}

/**
 * Reads a billing record the way the API writes it. Traced through
 * pithplay-rephoenix-backend:
 *
 * - The Stripe webhooks (stripeEventProcessor.applySubscriptionState) write
 *   providerSubscriptionId and the legacy subscriptionId, and map Stripe's
 *   status: active/trialing -> ACTIVE, past_due/unpaid/paused -> PAST_DUE,
 *   canceled -> CANCELLED. A failed invoice sets GRACE_PERIOD.
 * - cancelMySubscription sets cancelAtPeriodEnd + CANCELLING: the
 *   subscription was paid through the period end and still exists until then.
 * - When a subscription ends, the subscription.deleted webhook and
 *   ExpiryService (run on every billing read) set planId 'basic' and status
 *   CANCELLED. ExpiryService unsets subscriptionId and monerisSubscriptionId
 *   but not providerSubscriptionId, so an ended Stripe subscription can still
 *   carry its id: the status, not the id, says it has ended.
 * - The old one-time Moneris checkout (paymentService) wrote subscriptionId as
 *   an order number, status ACTIVE and a period end 30 days out that nothing
 *   renews. Moneris *recurring* subscriptions look the same (a bare
 *   subscriptionId) and keep billing, but their renewals never move
 *   currentPeriodEnd in production, so the two can't be told apart here.
 * - 'basic' was a paid $0.99/month plan before it became Free, and existing
 *   Basic subscriptions were not migrated, so 'basic' with a live
 *   subscription still bills. The API's own checkout guard
 *   (startSubscription) treats ACTIVE + any subscription id as an active
 *   subscription too.
 *
 * So: a subscription may bill whenever the record has a subscription id and
 * its status isn't CANCELLED, and anything that may bill can always be
 * cancelled. A period end long past only changes the wording
 * (renewalUnconfirmed); hiding Cancel from someone still being charged is the
 * one mistake this page must never make. The API refuses a cancellation it
 * can't carry out, and the error sends the customer to support.
 */
export const summarizeBilling = (
  billing: BillingInfo | null | undefined,
  plans: SubscriptionPlan[],
  now: Date = new Date()
): BillingSummary => {
  const planId = billing?.planId || FREE_PLAN_ID;
  const plan = plans.find((candidate) => candidate.id === planId);
  const planIsFree = plan ? isFreePlan(plan) : planId === FREE_PLAN_ID;
  const status = String(billing?.status || '').trim().toUpperCase();
  const subscriptionRef =
    billing?.providerSubscriptionId || billing?.subscriptionId || billing?.monerisSubscriptionId || null;
  const periodEnd = parseDate(billing?.currentPeriodEnd);
  const periodEndPassed = periodEnd ? periodEnd.getTime() <= now.getTime() : false;
  const ending = Boolean(billing?.cancelAtPeriodEnd) || status === 'CANCELLING';
  const hasPaymentIssue = status === 'PAST_DUE' || status === 'GRACE_PERIOD';

  const hasLiveSubscription = Boolean(subscriptionRef) && status !== 'CANCELLED';
  const renewalUnconfirmed = Boolean(
    hasLiveSubscription &&
      periodEnd &&
      status === 'ACTIVE' &&
      !ending &&
      now.getTime() - periodEnd.getTime() > STALE_SUBSCRIPTION_DAYS * DAY_MS
  );
  const isLegacyPaidBasic = hasLiveSubscription && planIsFree;

  const freePlan = plans.find((candidate) => candidate.id === FREE_PLAN_ID) || plans.find((candidate) => isFreePlan(candidate));
  const scheduledPlanId =
    billing?.changePlanAtPeriodEnd && billing?.nextPlanId && billing.nextPlanId !== planId ? billing.nextPlanId : null;

  let phase: SubscriptionPhase;
  if (!hasLiveSubscription) phase = 'none';
  else if (ending) phase = 'ending';
  else if (hasPaymentIssue) phase = 'payment_issue';
  else if (scheduledPlanId) phase = 'changing';
  else phase = 'renewing';

  // A cancellation lands on Free; a scheduled change lands on its next plan.
  const nextPlanId =
    phase === 'ending' ? (planIsFree ? null : freePlan?.id || FREE_PLAN_ID) : phase === 'changing' ? scheduledPlanId : null;
  const nextPlan = nextPlanId ? plans.find((candidate) => candidate.id === nextPlanId) : undefined;
  const nextPlanName = nextPlanId ? nextPlan?.name || formatPlanIdForDisplay(nextPlanId) : null;

  return {
    planId,
    plan,
    planName: isLegacyPaidBasic ? 'Basic' : plan?.name || formatPlanIdForDisplay(planId),
    planIsFree,
    status,
    hasLiveSubscription,
    isLegacyPaidBasic,
    hasStaleSubscriptionRef: Boolean(subscriptionRef) && !hasLiveSubscription,
    renewalUnconfirmed,
    phase,
    periodEnd,
    periodEndLabel: periodEnd ? formatDate(periodEnd) : null,
    periodEndPassed,
    nextPlanId,
    nextPlan,
    nextPlanName,
    monthlyPrice: hasLiveSubscription && plan && !planIsFree ? Number(plan.price) : null,
    currency: billingCurrency(billing?.currency),
    canCancel: hasLiveSubscription && !ending,
    canKeepPlan: hasLiveSubscription && ending,
    hasPaymentIssue: hasLiveSubscription && hasPaymentIssue,
  };
};

export interface StatusDescription {
  /** The status chip, or null when there is no subscription to describe. */
  chip: { status: string; label?: string; tone?: StatusTone } | null;
  /** One sentence: "Renews Nov 15, 2026 · CA$14.99 per month + tax". */
  text: string;
  /** A quieter line under it, when there's more the customer should know. */
  detail: string | null;
}

/** Shown on a legacy paid Basic subscription. */
export const LEGACY_BASIC_NOTE =
  'This is the original paid Basic plan, which now has the same limits as Free. If you cancel, you keep your work and stay on Free.';

/**
 * After a failed payment. The API cancels at the period end, and Stripe keeps
 * retrying the unpaid invoice until then, so a past-due customer who cancels
 * may still be charged for the period they've had. Never promise "you won't
 * be charged again" in that state; promise only what is true.
 */
export const OUTSTANDING_PAYMENT_NOTE = "Your last payment is still due, but you won't be charged for any later months.";

/** Shown when an ACTIVE subscription has no renewal on file (see STALE_SUBSCRIPTION_DAYS). */
export const STALE_SUBSCRIPTION_NOTE =
  "If you're still being charged for it, cancel it here or email info@artbits.solutions and we'll stop it.";

/** The current plan's status in words, for the plan panel. */
export const describeStatus = (summary: BillingSummary): StatusDescription => {
  const date = summary.periodEndLabel;
  const price = summary.monthlyPrice !== null ? `${formatMonthlyPrice(summary.monthlyPrice, summary.currency)} + tax` : null;

  switch (summary.phase) {
    case 'renewing': {
      if (summary.renewalUnconfirmed) {
        // Not "Active", and no renewal date we can't vouch for.
        return {
          chip: null,
          text: `No renewal recorded since ${date}`,
          detail: STALE_SUBSCRIPTION_NOTE,
        };
      }
      const renews = date && !summary.periodEndPassed ? `Renews ${date}` : null;
      return {
        chip: { status: 'ACTIVE' },
        text: [renews, price].filter(Boolean).join(' · ') || 'Renews monthly',
        detail: summary.isLegacyPaidBasic ? LEGACY_BASIC_NOTE : null,
      };
    }
    case 'ending':
      return {
        chip: { status: summary.status || 'CANCELLING', label: 'Ending', tone: 'attention' },
        text: `${date ? `Ends ${date}` : 'Ends at the end of your billing period'}, then Free`,
        detail: summary.hasPaymentIssue
          ? summary.planIsFree
            ? OUTSTANDING_PAYMENT_NOTE
            : `You keep ${summary.planName} until then. ${OUTSTANDING_PAYMENT_NOTE}`
          : summary.planIsFree
            ? "You won't be charged again."
            : `You keep ${summary.planName} until then and won't be charged again.`,
      };
    case 'changing': {
      const nextPrice =
        summary.nextPlan && !isFreePlan(summary.nextPlan) ? formatPlanPrice(summary.nextPlan, summary.currency) : null;
      return {
        chip: { status: 'ACTIVE' },
        text: `Switches to ${summary.nextPlanName} ${date ? `on ${date}` : 'at the end of your billing period'}`,
        detail: nextPrice
          ? `You keep ${summary.planName} until then. After that, ${summary.nextPlanName} is ${nextPrice}.`
          : `You keep ${summary.planName} until then.`,
      };
    }
    case 'payment_issue':
      return {
        chip: { status: summary.status, label: 'Payment issue', tone: 'problem' },
        text: `Update your card to keep ${summary.planName}`,
        detail: "We couldn't take your last payment and will try again. Update your card so it goes through.",
      };
    case 'none':
    default:
      if (summary.planIsFree) {
        return {
          chip: null,
          text: 'Free plan · no card needed',
          detail: null,
        };
      }
      if (summary.status === 'CANCELLED') {
        return { chip: { status: 'CANCELLED' }, text: 'Your subscription has ended', detail: null };
      }
      return { chip: null, text: 'No active subscription', detail: null };
  }
};

/** "on Nov 15, 2026", or "at the end of your billing period" without a date. */
export const atPeriodEndPhrase = (summary: Pick<BillingSummary, 'periodEndLabel'>): string =>
  summary.periodEndLabel ? `on ${summary.periodEndLabel}` : 'at the end of your billing period';

/** "Nov 15, 2026", or "the end of your billing period" without a date. */
export const periodEndOrFallback = (summary: Pick<BillingSummary, 'periodEndLabel'>): string =>
  summary.periodEndLabel || 'the end of your billing period';

/** The cheapest plan that costs more than the current one and allows more of `pick`. */
export const nextPlanUp = (
  plans: SubscriptionPlan[],
  currentPlanId: string,
  pick: (limits: PlanLimits) => number | null | undefined
): SubscriptionPlan | undefined => {
  const current = plans.find((plan) => plan.id === currentPlanId);
  const currentPrice = Number(current?.price) || 0;
  const currentValue = current ? pick(current.limits) : undefined;
  const allowsMore = (value: number | null | undefined) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return false;
    if (currentValue === null || currentValue === undefined || !Number.isFinite(currentValue)) return true;
    if (currentValue < 0) return false;
    return value < 0 || value > currentValue;
  };
  return sortPlansByPrice(plans).find(
    (plan) => plan.id !== currentPlanId && (Number(plan.price) || 0) > currentPrice && allowsMore(pick(plan.limits))
  );
};

/** What happens as storage fills up, from 80% used (UsageMeter shows it from there). */
export const storageHint = (used: number, limit: number, upgrade?: SubscriptionPlan): string | undefined => {
  if (!(limit > 0) || !Number.isFinite(used)) return undefined;
  const ratio = used / limit;
  const blockedAt = formatBytes(limit * (1 + STORAGE_OVERAGE_ALLOWANCE));
  const upgradeStorage = upgrade?.limits.storage;
  const upgradePart = upgrade
    ? `, or upgrade to ${upgrade.name} for ${
        upgradeStorage !== undefined && upgradeStorage < 0 ? 'unlimited storage' : formatBytes(upgradeStorage)
      }`
    : '';
  if (ratio >= 1 + STORAGE_OVERAGE_ALLOWANCE) {
    return `New uploads are paused until you're under ${blockedAt}. Delete footage you don't need${upgradePart}.`;
  }
  if (ratio >= 1) {
    return `You're over your ${formatBytes(limit)}. New uploads stop at ${blockedAt}. Delete footage you don't need${upgradePart}.`;
  }
  return `New uploads stop at ${blockedAt}, 10% over your plan. Delete footage you don't need${upgradePart}.`;
};

/** What happens as active projects fill up. Approved, delivered and cancelled projects don't count. */
export const projectsHint = (used: number, limit: number, upgrade?: SubscriptionPlan): string | undefined => {
  if (!(limit > 0) || !Number.isFinite(used)) return undefined;
  const more =
    upgrade && upgrade.limits.projects < 0 ? 'unlimited active projects' : upgrade ? `${upgrade.limits.projects} active projects` : '';
  if (used >= limit) {
    return `Start another project once one is approved, delivered or cancelled${
      upgrade ? `, or upgrade to ${upgrade.name} for ${more}` : ''
    }.`;
  }
  return `New projects stop at ${limit} active.${upgrade ? ` Upgrade to ${upgrade.name} for ${more}.` : ''}`;
};

/** Labels and tones for invoice statuses (the API passes Stripe's through, or "paid"). */
export const invoiceStatusMeta = (status?: string | null): { label: string; tone: StatusTone } => {
  const key = String(status || 'paid').trim().toLowerCase();
  switch (key) {
    case 'paid':
    case 'succeeded':
      return { label: 'Paid', tone: 'done' };
    case 'open':
    case 'pending':
      return { label: 'Due', tone: 'attention' };
    case 'uncollectible':
    case 'failed':
      return { label: 'Not paid', tone: 'problem' };
    case 'refunded':
      return { label: 'Refunded', tone: 'neutral' };
    case 'void':
    case 'voided':
      return { label: 'Void', tone: 'neutral' };
    case 'draft':
      return { label: 'Draft', tone: 'neutral' };
    default: {
      const words = key.replace(/[_-]+/g, ' ');
      return { label: words.charAt(0).toUpperCase() + words.slice(1), tone: 'neutral' };
    }
  }
};

// API errors that read well as they are. Anything else (raw statuses, ids,
// provider exceptions) is replaced by the caller's plain-language fallback.
const CUSTOMER_SAFE_ERRORS = [
  /this is an editor account/i,
  /^too many /i,
  /\balready\b/i,
  /pending cancellation|cancellation is pending/i,
  /billing period has already ended/i,
  /doesn't need checkout|does not need checkout/i,
  // A declined upgrade: Stripe's own card message, then that nothing changed.
  /your plan hasn't changed/i,
  // A plan given for free has no subscription to change.
  /given to you at no charge/i,
];
const DEVELOPER_WORDING = /[`=<>{}]|\b[a-z]+_[A-Za-z0-9]{6,}\b|\bstatus\b|endpoint|mutation|provider|\bundefined\b|\bnull\b|stripe|moneris/i;

/** The API's message when a customer can act on it, otherwise `fallback`. */
export const friendlyBillingError = (raw: unknown, fallback: string): string => {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text || text.length > 240 || DEVELOPER_WORDING.test(text)) return fallback;
  return CUSTOMER_SAFE_ERRORS.some((pattern) => pattern.test(text)) ? text : fallback;
};
